import { DocumentType } from '@bulthaup/api/db';
import { Button, Divider, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconEye, IconPlus, IconTrash } from '@tabler/icons-react';
import { reverse } from 'lodash';

import {
  ServiceOrderForm,
  ServiceOrderFormValues,
} from '../../../-components/service-order/-service-order-form';
import { useUser } from '../../../../../../../hooks/use-user';
import { RouterOutput, trpc } from '../../../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../../../services/upload';
import { formatDate } from '../../../../../../../utils/format';
import {
  ServiceOrderRequestForm,
  ServiceOrderRequestFormValues,
} from './-service-order-request-form';

export function ServiceOrderRequestSection({
  project,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
}) {
  const { user } = useUser();
  const deleteServiceOrderRequest =
    trpc.projects.deleteServiceOrderRequest.useMutation({
      onSuccess: () => {
        notifications.show({
          title: "Fiche d'intervention supprimée",
          message: '',
          color: 'green',
        });
      },
    });

  const createServiceOrderRequest =
    trpc.projects.createServiceOrderRequest.useMutation({
      onSuccess: () => {
        modals.closeAll();
        notifications.show({
          title: "Fiche d'intervention créée avec succès",
          message: '',
          color: 'green',
        });
      },
    });

  const updateServiceOrderRequest =
    trpc.projects.updateServiceOrderRequest.useMutation({
      onSuccess: () => {
        modals.closeAll();
        notifications.show({
          title: "Fiche d'intervention modifiée avec succès",
          message: '',
          color: 'green',
        });
      },
    });

  const deleteServiceOrderRequestMutation =
    trpc.projects.deleteServiceOrderRequest.useMutation();

  const deleteServiceOrder = trpc.documents.deleteServiceOrder.useMutation({
    onSuccess: () => {
      notifications.show({
        title: "Fiche d'intervention supprimée",
        message: '',
        color: 'green',
      });
    },
  });

  const generateServiceOrder = trpc.documents.generateServiceOrder.useMutation({
    onSuccess: async (_, variables) => {
      // Delete the ServiceOrderRequest after successfully creating the ServiceOrder document
      await deleteServiceOrderRequestMutation.mutateAsync({
        id: variables.serviceOrderRequestId,
      });

      modals.closeAll();
      notifications.show({
        title: "Fiche d'intervention remplie avec succès",
        message: '',
        color: 'green',
      });
    },
  });

  const previewServiceOrder = trpc.documents.previewServiceOrder.useMutation({
    onSuccess: async () => console.log('open PDF'),
    onError: error => console.error(error),
  });

  const serviceOrderRequests = project.serviceOrderRequests || [];
  const serviceOrderDocuments = project.documents.filter(
    document => document.type === DocumentType.SERVICE_ORDER,
  );

  type Project = RouterOutput['projects']['get'];
  type ServiceOrderRequest = NonNullable<Project>['serviceOrderRequests'][0];

  const openFillOutModal = (serviceOrderRequest: ServiceOrderRequest) => {
    modals.open({
      title: "Remplir la fiche d'intervention",
      size: 'xl',
      children: (
        <ServiceOrderForm
          project={project}
          serviceOrderRequest={serviceOrderRequest}
          onSubmit={async (values: ServiceOrderFormValues) => {
            await generateServiceOrder.mutateAsync({
              projectId: project.id,
              serviceOrderRequestId: serviceOrderRequest.id,
              date: new Date(values.date),
              time: values.time,
              attendees: values.attendees,
              extraAttendee: values.extraAttendee,
              answer1: values.answer1,
              answer2: values.answer2,
              answer3: values.answer3,
              answer4: values.answer4,
              answer5: values.answer5,
              answer6: values.answer6,
              observations: values.observations,
              signer: values.signer,
              signatureUtFileKey: values.signatureUtFileKey,
              imagesUtFileKeys: values.imagesUtFileKeys,
              from: `${user.firstName} ${user.lastName}`,
            });
          }}
        />
      ),
    });
  };

  const openCreateModal = () => {
    modals.open({
      title: "Ajouter une fiche d'intervention",
      size: 'xl',
      children: (
        <ServiceOrderRequestForm
          onCancel={() => modals.closeAll()}
          onSubmit={async values => {
            await createServiceOrderRequest.mutateAsync({
              projectId: project.id,
              question1: values.question1,
              question2: values.question2,
              question3: values.question3,
              question4: values.question4,
              question5: values.question5,
              question6: values.question6,
              ...(values.date && { date: new Date(values.date) }),
              ...(values.time && { time: values.time }),
            });
          }}
        />
      ),
    });
  };

  const openEditModal = (serviceOrderRequest: ServiceOrderRequest) => {
    modals.open({
      title: "Modifier la fiche d'intervention",
      size: 'xl',
      children: (
        <ServiceOrderRequestForm
          initialValues={{
            question1: serviceOrderRequest.question1,
            question2: serviceOrderRequest.question2,
            question3: serviceOrderRequest.question3,
            question4: serviceOrderRequest.question4,
            question5: serviceOrderRequest.question5,
            question6: serviceOrderRequest.question6,
            date: serviceOrderRequest.date
              ? serviceOrderRequest.date.toISOString()
              : undefined,
            time: serviceOrderRequest.time || undefined,
          }}
          onCancel={() => modals.closeAll()}
          onSubmit={async (values: ServiceOrderRequestFormValues) => {
            await updateServiceOrderRequest.mutateAsync({
              id: serviceOrderRequest.id,
              ...values,
              date: values.date ? new Date(values.date) : undefined,
            });
          }}
        />
      ),
    });
  };

  const previewDocument = async (serviceOrderRequest: ServiceOrderRequest) => {
    try {
      const pdf = await previewServiceOrder.mutateAsync({
        projectId: serviceOrderRequest.projectId,
        serviceOrderRequestId: serviceOrderRequest.id,
        from: `${user.firstName} ${user.lastName}`,
      });

      if (pdf) {
        const type = 'application/pdf';
        const blob = new Blob([new Uint8Array(pdf)], { type });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Stack>
      <Group>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="outline"
          onClick={openCreateModal}
        >
          Ajouter une fiche d'intervention
        </Button>
      </Group>
      {serviceOrderRequests.length > 0 && (
        <Stack>
          {serviceOrderRequests.map(request => (
            <Group key={request.id} align="center" justify="space-between">
              <Stack gap={0} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Fiche du {formatDate(request.date || request.createdAt)}{' '}
                  {request.time ? `à ${request.time}` : ''}
                </Text>
                <Text c="dimmed" size="xs" truncate>
                  {request.question1 || 'Aucune information'}
                </Text>
                <Button
                  mt="xs"
                  size="xs"
                  onClick={() => openFillOutModal(request)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Remplir
                </Button>
              </Stack>
              <Group gap="xs">
                <Stack gap="xs">
                  <Group gap="xs">
                    <Button
                      variant="outline"
                      size="xs"
                      leftSection={<IconEdit size={14} />}
                      onClick={() => openEditModal(request)}
                    >
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      leftSection={<IconEye size={16} />}
                      onClick={() => previewDocument(request)}
                    >
                      Voir
                    </Button>
                  </Group>
                  {/* <Button
                    size="xs"
                    onClick={() => openFillOutModal(request)}
                    style={{ alignSelf: 'flex-end' }}
                  >
                    Remplir
                  </Button> */}
                </Stack>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={async () => {
                    if (
                      confirm(
                        "Voulez-vous vraiment supprimer cette fiche d'intervention ?\n\nCette action est irréversible.",
                      )
                    ) {
                      await deleteServiceOrderRequest.mutateAsync({
                        id: request.id,
                      });
                    }
                  }}
                >
                  <IconTrash size={14} />
                </Button>
              </Group>
            </Group>
          ))}
        </Stack>
      )}

      {serviceOrderDocuments.length > 0 && (
        <Stack gap="md">
          {serviceOrderRequests.length > 0 && <Divider />}
          <Stack gap="md">
            <Text size="sm" fw={700}>
              <b>Fiches d'intervention remplies</b>
            </Text>
          </Stack>
          {reverse([...serviceOrderDocuments]).map((document, index) => (
            <Group key={document.id} align="center" justify="space-between">
              <Stack gap={0}>
                <Text size="sm">Fiche n°{index + 1}</Text>
                <Text c="dimmed" size="xs" truncate style={{ flex: 1 }}>
                  le {formatDate(document.createdAt)}
                </Text>
              </Stack>
              <Group gap="xs">
                <Button
                  leftSection={<IconEye size={16} />}
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    window.open(getUTDocumentUrl(document.utFileKey), '_blank');
                  }}
                >
                  Voir
                </Button>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={async () => {
                    if (
                      confirm(
                        "Voulez-vous vraiment supprimer cette fiche d'intervention ?\n\nCette action est irréversible.",
                      )
                    ) {
                      await deleteServiceOrder.mutateAsync({
                        documentId: document.id,
                      });
                    }
                  }}
                >
                  <IconTrash size={14} />
                </Button>
              </Group>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
